import { useTheme } from '@mui/material';
import axios from 'axios';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import CropProtectionFilters from '/@/components/catalog/crop-protection-filters';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { resetProductSelection, setCatalogZone } from '/@/store/catalog/actions';
import { useCatalogZones } from '/@/store/catalog/apiProviders';
import { getCatalogBrands, getCatalogZone, getCatalogZonesByBrand } from '/@/store/catalog/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import { isMobile } from '/@/utils';
import { reverse } from '/@/utils/router';
import Select from '/@/v3/components/forms/select';
import Title from '/@/v3/components/title';
import styles from './styles.module.css';

const CatalogSidebar = ({ brand, internal, children, options }) => {
    const catalogBrands = useSelector(getCatalogBrands);
    const theme = useTheme();
    const brands = catalogBrands.map((catalogBrand) => {
        let brandName;
        if (catalogBrands.filter((el) => el.name === catalogBrand.name).length > 1) {
            brandName = `${catalogBrand.name} (${_(catalogBrand.type === 'corn' ? _('Maíz') : _('Soja'))})`;
        } else {
            brandName = catalogBrand.name;
        }
        return {
            label: brandName,
            value: catalogBrand.id,
        };
    });

    const dispatch = useDispatch();
    const catalogZonesQuery = useCatalogZones({ page: 1, pageSize: 10000, crop: brand.type, type: 'catalog' });
    const catalogZones = catalogZonesQuery.data?.results || [];

    const zone = useSelector((state) => getCatalogZone(state, catalogZones));
    const zones = useSelector((state) => getCatalogZonesByBrand(state, brand, 'catalog', catalogZones));
    const [svgContent, setSvgContent] = useState('');

    useEffect(() => {
        const fetchSvg = async () => {
            const response = await axios.get(zone.image);
            let svg: string = response.data;

            // Strip script tags and event handlers before injecting SVG markup
            svg = svg.replace(/<script[\s\S]*?<\/script>/gi, '');
            svg = svg.replace(/\son\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '');
            svg = svg.replace(/<use[^>]+href\s*=\s*["'][^"']*["'][^>]*>/gi, '');

            svg = svg.replace(/(<path[^>]*fill=")[^"]*"/g, `$1${theme.palette.primary.main}"`);
            svg = svg.replace(/(<svg[^>]*)(width|height)="[^"]*"([^>]*>)/g, '$1$3');
            svg = svg.replace('<svg', '<svg width="100px" height="200px"');
            setSvgContent(svg);
        };

        if (zone) {
            fetchSvg();
        }
    }, [zone, theme]);
    const country = useSelector(getSessionCountry);
    const history = useNavigate();

    const handleChangeBrand = (brandId) => {
        const selectedBrand = catalogBrands.find((selectedBrand) => selectedBrand.id === brandId);
        const type = selectedBrand.type;
        const slug = selectedBrand.slug;

        if (brand.id !== selectedBrand.id) {
            history(
                reverse('catalog.ProductSelection', {
                    params: { type, slug, internal },
                })
            );
            dispatch(bindActionCreators(resetProductSelection, dispatch));
        }
    };

    const renderZones = () => {
        const enableZoneSelect = (country.code === 'BR' && brand.type == 'soy') || isMobile();

        return (
            <div>
                {zones.length > 1 && (
                    <div className={!enableZoneSelect ? styles.zones : null}>
                        {enableZoneSelect ? (
                            <div className={styles.selectContainer}>
                                <Select
                                    value={zone && zone.id}
                                    options={zones.map((zone) => ({ label: _(zone.name), value: zone.id }))}
                                    clearable={false}
                                    color={settings.app.theme}
                                    onChange={(e) => dispatch(setCatalogZone(e))}
                                    maxHeight='300px'
                                    colorValue
                                    className={styles[`zone-${settings.app.theme}`]}
                                />
                            </div>
                        ) : (
                            <div className={styles.zones}>
                                {zones.map((zoneItem) => {
                                    return (
                                        <div
                                            onClick={() => dispatch(setCatalogZone(zoneItem.id))}
                                            key={zoneItem.id}
                                            className={
                                                zone &&
                                                zoneItem.id === zone.id &&
                                                styles[`selected-${settings.app.theme}`]
                                            }
                                        >
                                            <Title
                                                size='sm'
                                                theme={zone && zoneItem.id === zone.id ? settings.app.theme : undefined}
                                                color={zone && zoneItem.id !== zone.id ? 'no-selected' : undefined}
                                                className={styles.zoneTitle}
                                                bolder
                                            >
                                                {_(zoneItem.name)}
                                            </Title>
                                        </div>
                                    );
                                })}
                                {zone && (
                                    <div key={zone.id} className={styles.zone}>
                                        {zone.image && (
                                            <div
                                                className={styles.image}
                                                dangerouslySetInnerHTML={{ __html: svgContent }}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };
    const disableZones = brand.name === 'Bayer LT' || brand.name === 'Bayer';

    return (
        <div>
            <div className={styles.selectContainer}>
                <Select
                    options={brands}
                    placeholder={_('Catálogo')}
                    persistPlaceholder
                    onChange={(e) => handleChangeBrand(e)}
                    className={classNames(styles.select, styles[`theme-${settings.app.theme}`])}
                    classNameValue={styles.value}
                    value={brand.id}
                />
            </div>
            {brand.type === 'crop_protection' && <CropProtectionFilters options={options} />}
            {children}
            {!disableZones && renderZones()}
        </div>
    );
};

export default CatalogSidebar;
